import React, { useState, useEffect } from 'react';
import db from '../database/db';
import './RecordsScreen.css';

function RecordsScreen({ userId }) {
  const [records, setRecords] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRecord, setNewRecord] = useState({ weight: '', reps: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadRecords = async () => {
    try {
      setLoading(true);
      const recordsData = await db.getBenchPressRecords(userId);
      setRecords(recordsData);
    } catch (error) {
      console.error('Error loading records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecord = async (e) => {
    e.preventDefault();
    
    const weight = parseFloat(newRecord.weight);
    const reps = parseInt(newRecord.reps);

    if (!weight || weight <= 0 || !reps || reps <= 0) {
      alert('Por favor, introduce valores válidos');
      return;
    }

    try {
      await db.createBenchPressRecord(userId, weight, reps);
      setNewRecord({ weight: '', reps: '' });
      setShowAddForm(false);
      await loadRecords();
    } catch (error) {
      console.error('Error adding record:', error);
      alert('Error al añadir el récord');
    }
  };

  const handleEditRecord = async (recordId, weight, reps) => {
    try {
      await db.updateBenchPressRecord(recordId, weight, reps);
      setEditingRecord(null);
      await loadRecords();
    } catch (error) {
      console.error('Error updating record:', error);
      alert('Error al actualizar el récord');
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este récord?')) {
      try {
        await db.deleteBenchPressRecord(recordId);
        await loadRecords();
      } catch (error) {
        console.error('Error deleting record:', error);
        alert('Error al eliminar el récord');
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="records-screen">
        <div className="loading">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="records-screen">
      <div className="records-container">
        <div className="records-header">
          <h1 className="records-title">Récords Press Banca</h1>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="btn-primary"
          >
            {showAddForm ? 'Cancelar' : '+ Añadir Récord'}
          </button>
        </div>

        {showAddForm && (
          <div className="add-record-form">
            <form onSubmit={handleAddRecord}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="new-weight">Peso (kg)</label>
                  <input
                    id="new-weight"
                    type="number"
                    step="0.5"
                    min="0"
                    value={newRecord.weight}
                    onChange={(e) => setNewRecord({ ...newRecord, weight: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="new-reps">Repeticiones</label>
                  <input
                    id="new-reps"
                    type="number"
                    min="1"
                    value={newRecord.reps}
                    onChange={(e) => setNewRecord({ ...newRecord, reps: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
              </div>
              <button type="submit" className="btn-primary">
                Guardar Récord
              </button>
            </form>
          </div>
        )}

        <div className="records-list">
          {records.length === 0 ? (
            <div className="no-records">
              <p>No hay récords registrados.</p>
              <p>Añade tu primer récord para comenzar.</p>
            </div>
          ) : (
            records.map((record) => (
              <div key={record.id} className="record-card">
                {editingRecord === record.id ? (
                  <RecordEditForm
                    record={record}
                    onSave={(weight, reps) => handleEditRecord(record.id, weight, reps)}
                    onCancel={() => setEditingRecord(null)}
                  />
                ) : (
                  <>
                    <div className="record-main">
                      <div className="record-weight">{record.weight} kg</div>
                      <div className="record-reps">{record.reps} reps</div>
                    </div>
                    <div className="record-date">
                      {formatDate(record.date)}
                    </div>
                    <div className="record-actions">
                      <button
                        onClick={() => setEditingRecord(record.id)}
                        className="btn-edit"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteRecord(record.id)}
                        className="btn-delete"
                      >
                        Eliminar
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function RecordEditForm({ record, onSave, onCancel }) {
  const [weight, setWeight] = useState(record.weight);
  const [reps, setReps] = useState(record.reps);

  const handleSubmit = (e) => {
    e.preventDefault();
    const weightValue = parseFloat(weight);
    const repsValue = parseInt(reps);
    
    if (!weightValue || weightValue <= 0 || !repsValue || repsValue <= 0) {
      alert('Por favor, introduce valores válidos');
      return;
    }
    
    onSave(weightValue, repsValue);
  };

  return (
    <form onSubmit={handleSubmit} className="edit-record-form">
      <div className="form-row">
        <div className="form-group">
          <label>Peso (kg)</label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="form-input"
            required
          />
        </div>
        <div className="form-group">
          <label>Repeticiones</label>
          <input
            type="number"
            min="1"
            value={reps}
            onChange={(e) => setReps(e.target.value)}
            className="form-input"
            required
          />
        </div>
      </div>
      <div className="form-actions">
        <button type="submit" className="btn-primary">
          Guardar
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancelar
        </button>
      </div>
    </form>
  );
}

export default RecordsScreen;
